import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";

import { useTranslation } from "react-i18next";
import { PurchaseCard } from "./PurchaseCard";
import { usePurchaseDisplay } from "./usePurchaseDisplay";
import { BlockPaginatedList } from "../BlockPaginatedList";
import { BlockEmptyState } from "../BlockEmptyState";

export interface PaymentsRecordsListProps {
  contact: TellescopeModelFromName<"endusers">;
  showDetails?: boolean;
  onSelectPurchase?: (purchase: TellescopeModelFromName<"purchases">) => void;
}

export const PaymentsRecordsList = ({ contact, showDetails = false, onSelectPurchase }: PaymentsRecordsListProps) => {
  const { t } = useTranslation("contacts");
  const getDisplayFields = usePurchaseDisplay();

  const purchasesQuery = useTellescopeInfiniteQuery({
    model: "purchases",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => data.pages.flat(),
  });

  if (purchasesQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <BlockEmptyState>{t("Loading purchases...")}</BlockEmptyState>
      </div>
    );
  }

  if (!purchasesQuery.data?.length) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <BlockEmptyState>{t("No purchases found")}</BlockEmptyState>
      </div>
    );
  }

  return (
    <BlockPaginatedList
      className="flex flex-col"
      scrollbar="floating"
      fadeWhenClipped
      onBottomReached={() => purchasesQuery.fetchNextPage()}
    >
      {purchasesQuery.data.map((purchase) => {
        const { amount, date, description } = getDisplayFields(purchase);

        return (
          <PurchaseCard
            key={purchase.id}
            title={purchase.title}
            amount={amount}
            description={description}
            date={showDetails ? date : undefined}
            className="mb-2"
            onClick={onSelectPurchase ? () => onSelectPurchase(purchase) : undefined}
          />
        );
      })}
    </BlockPaginatedList>
  );
};
